/**
 * InventoryPanel — two ribbons:
 *   • Items — stacking collectables (cores, dark matter, warp fuel) with counts.
 *   • Ship's Modification — non-stacking gear (shield generators) with per-item
 *     stats. Newly-dropped items are highlighted; opening clears the badge.
 */
import { useEffect, useState } from "react";
import type { HudSnapshot } from "@/engine/GameState";
import { RARITY_META } from "@/game/mods/modifications";
import { itemIcon } from "./itemIcons";
import { t, useLang } from "@/i18n";

const BASE = import.meta.env.BASE_URL;

interface ItemDef {
  id: string;
  name: string;
  fallback: string;
  desc: string;
}

const ITEMS: ItemDef[] = [
  {
    id: "moonCore",
    name: "Moon Core",
    fallback: "◆",
    desc: "Dropped from moons (10%) / sub-moons (5%). Used to craft Energy Cores.",
  },
  {
    id: "energyCore",
    name: "Energy Core",
    fallback: "⬢",
    desc: "Crafted at a mechanical planet. Required to refit the mothership (Mk II / Mk III).",
  },
  {
    id: "darkMatter",
    name: "Dark Matter",
    fallback: "✦",
    desc: "5% drop from destroying any asteroid. Used to craft Warp Drive Fuel.",
  },
  {
    id: "warpFuel",
    name: "Warp Drive Fuel",
    fallback: "⛽",
    desc: "Single-use. Consumed by Warp to jump to a new solar system. Craft it or buy it at a market.",
  },
  {
    id: "eowHead",
    name: "Eater of Worlds — Head",
    fallback: "👹",
    desc: "Trophy from slaying the Eater of Worlds (5% drop). Reserved for a future Mk VII refit.",
  },
  {
    id: "eowBody",
    name: "Eater of Worlds — Segment",
    fallback: "◉",
    desc: "A segment of the Eater of Worlds (20% drop). Reserved for a future Mk VII refit.",
  },
  {
    id: "eowTail",
    name: "Eater of Worlds — Tail",
    fallback: "⬖",
    desc: "The Eater of Worlds' tail (10% drop). Reserved for a future Mk VII refit.",
  },
  {
    id: "eowEnergyCore",
    name: "Eater's Core",
    fallback: "◈",
    desc: "The Eater of Worlds' beating heart (1% drop). Reserved for a future Mk VII refit.",
  },
];

interface Props {
  snap: HudSnapshot;
  onSeen: () => void;
  onAssignPilot: (slot: number, crewId: string) => void;
  onUnassignPilot: (slot: number) => void;
  onClose: () => void;
}

export default function InventoryPanel({
  snap,
  onSeen,
  onAssignPilot,
  onUnassignPilot,
  onClose,
}: Props) {
  const [tab, setTab] = useState<"items" | "mods" | "pilots">("items");
  useLang();
  useEffect(() => {
    onSeen();
  }, [onSeen]);
  const inv = snap.inventory;
  const anything = ITEMS.some((it) => (inv[it.id] ?? 0) > 0);
  const mods = snap.mods;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="stats-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Inventory"
      >
        <div className="upg-head">
          <div className="upg-head-title">🎒 {t("Inventory")}</div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="trade-tabs">
          <button className={tab === "items" ? "on" : ""} onClick={() => setTab("items")}>
            {t("Items")}
          </button>
          <button className={tab === "mods" ? "on" : ""} onClick={() => setTab("mods")}>
            {t("Mods")}
          </button>
          <button className={tab === "pilots" ? "on" : ""} onClick={() => setTab("pilots")}>
            {t("Pilots")}
          </button>
        </div>
        <div className="stats-body">
          {tab === "items" && (
            <>
              {ITEMS.filter((it) => (inv[it.id] ?? 0) > 0).map((it) => {
                const url = itemIcon(it.id);
                const isNew = snap.newItems.includes(it.id);
                return (
                  <div className={`inv-row${isNew ? " is-new" : ""}`} key={it.id}>
                    {url ? (
                      <img className="inv-img" src={url} alt="" />
                    ) : (
                      <span className="inv-icon">{it.fallback}</span>
                    )}
                    <div className="inv-info">
                      <div className="inv-name">
                        {t(it.name)}{" "}
                        <span className="inv-count">×{inv[it.id] ?? 0}</span>
                        {isNew && <span className="inv-new">{t("NEW")}</span>}
                      </div>
                      <div className="inv-desc">{t(it.desc)}</div>
                    </div>
                  </div>
                );
              })}
              {!anything && (
                <div className="inv-empty">
                  {t("No items yet — destroy moons and asteroids to find materials.")}
                </div>
              )}
            </>
          )}
          {tab === "mods" && (
            <>
              {mods.length === 0 && (
                <div className="inv-empty">
                  {t(
                    "No modifications yet — build shield generators at a 🛒 trade hub (Build Mods).",
                  )}
                </div>
              )}
              {mods.map((m) => {
                const col = RARITY_META[m.rarity].color;
                const on = m.id === snap.equippedShieldModId;
                return (
                  <div className="inv-row" key={m.id}>
                    <span className="inv-icon" style={{ color: col }}>
                      🛡
                    </span>
                    <div className="inv-info">
                      <div className="inv-name">
                        {t(RARITY_META[m.rarity].label)} {t("Shield Generator")}
                        {on && <span className="inv-new">{t("EQUIPPED")}</span>}
                      </div>
                      <div className="inv-desc">
                        <span style={{ color: col, fontWeight: 700 }}>
                          {t(RARITY_META[m.rarity].label)}
                        </span>{" "}
                        · +{m.shieldBonus} {t("max shield")} · {t("tier")} {m.tier}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {tab === "pilots" && (
            <>
              {snap.fleet.slots === 0 || snap.fleet.fighters.every((f) => !f) ? (
                <div className="inv-empty">
                  {t(
                    "No fighters yet — buy fighters at the 🔧 Arsenal (Mk IV), then assign pilots recruited at a 🍺 Space Tavern here.",
                  )}
                </div>
              ) : (
                snap.fleet.fighters.map((f, i) =>
                  !f ? null : (
                    <div className="inv-row" key={i}>
                      <img
                        className="inv-img"
                        src={
                          f.pilot
                            ? `${BASE}portraits/${f.pilot.portrait}.png`
                            : `${BASE}sprites/player_fighter.png`
                        }
                        alt=""
                      />
                      <div className="inv-info">
                        <div className="inv-name">
                          {t("Fighter Bay")} {i + 1}
                          <span
                            className={`inv-status ${
                              f.kia ? "kia" : f.grounded ? "grounded" : "ready"
                            }`}
                          >
                            {f.kia ? t("K.I.A.") : f.grounded ? t("GROUNDED") : t("READY")}
                          </span>
                        </div>
                        {f.kia && f.pilot ? (
                          <div className="inv-desc">
                            <span
                              style={{
                                color: RARITY_META[f.pilot.rarity].color,
                                fontWeight: 700,
                              }}
                            >
                              {f.pilot.name}
                            </span>{" "}
                            · {t("Killed in action — reroll at a 🍺 tavern to replace.")}
                          </div>
                        ) : f.pilot ? (
                          <div className="inv-desc">
                            <span
                              style={{
                                color: RARITY_META[f.pilot.rarity].color,
                                fontWeight: 700,
                              }}
                            >
                              {f.pilot.name}
                            </span>{" "}
                            · {f.pilot.buff}
                            <button
                              className="inv-mini-btn"
                              onClick={() => onUnassignPilot(i)}
                            >
                              {t("Remove")}
                            </button>
                          </div>
                        ) : snap.fleet.availablePilots.length > 0 ? (
                          <select
                            className="fleet-select"
                            defaultValue=""
                            onChange={(e) =>
                              e.target.value && onAssignPilot(i, e.target.value)
                            }
                          >
                            <option value="" disabled>
                              {t("Assign pilot…")}
                            </option>
                            {snap.fleet.availablePilots.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name} ({t(RARITY_META[p.rarity].label)} T{p.tier})
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="inv-desc">
                            {t("No pilot — recruit at a 🍺 Space Tavern.")}
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
